/**
 * Recovery kit (docs/ux/workflow-simplification.md B1 step 3, docs/e2e.md "Recovery kit"): one
 * file the browser owns, holding the account id, the sign-in recovery codes and a 256-bit data
 * recovery key. The data key opens the account's `recovery` escrow recipient (the genesis recovery
 * wrap), so losing every device and passkey still recovers encrypted work.
 *
 * - At sign-up there is no directory yet, so the escrow is prepared locally (ciphertext and a
 *   public key only; the data key itself is never stored) and joins the genesis directory in the
 *   same passkey tap that approves the first machine.
 * - Regenerating replaces the recovery recipient (which rotates the epoch and drops the old escrow
 *   at the hub) and every sign-in code, so the old kit stops working entirely.
 * - Restoring signs in with the kit's codes, then releases the escrow after the 24 h hold (or at
 *   once when a trusted device approves). The pending request survives a new session through the
 *   device-bound session, never through the data key, which is only ever in memory.
 */
import type { SaveRequest } from '@1password/save-button'
import {
  createDirectoryEscrow,
  createRecoveryCode,
  derivePrfKek,
  deriveRecoveryKek,
  type EcPublicJwk,
  type KeyRecipient,
  signP256,
  signStatement,
  wrapAccountKey,
} from '@viko/crypto'
import { SESSION_DEVICE_DOMAIN } from '@viko/protocol'
import { z } from 'zod'
import { type Api, ApiError } from './api.js'
import { keysApi } from './content-keys-api.js'
import { addEscrow, type RecoveryResult, recoverAccess, unlocked } from './content-recovery.js'
import type { BrowserContentSession } from './content-session.js'
import { loadContentTrust } from './content-trust.js'
import { loadDevice, type StoredDevice } from './device-key.js'
import { idb } from './idb.js'
import type { PasskeyProvider } from './passkeys.js'

export interface RecoveryKit {
  accountId: string
  email?: string
  /** Sign-in recovery codes (each works once). */
  codes: string[]
  /** 256-bit base64url data recovery key; absent only when this browser couldn't create one. */
  dataKey?: string
  createdAt: number
}

export const KIT_TITLE = 'Viko recovery kit'
const HEADER = `${KIT_TITLE} v1`

/** Local-only text used by the download and the 1Password save request. */
export function recoveryKitText(kit: RecoveryKit): string {
  return [
    HEADER,
    `Created: ${new Date(kit.createdAt).toISOString().slice(0, 10)}`,
    `Account id: ${kit.accountId}`,
    ...(kit.email ? [`Email: ${kit.email}`] : []),
    '',
    'Data recovery key (unlocks your encrypted work if you lose every device):',
    kit.dataKey ?? '(none yet: create one in You › Security)',
    '',
    'Sign-in recovery codes (each works once):',
    ...kit.codes,
    '',
    'Keep this file private and offline, or in your password manager. It replaces any earlier kit.',
    'Anyone holding it can start a recovery; your signed-in devices are alerted and encrypted work',
    'is released only after a 24-hour hold unless one of them approves.',
    '',
  ].join('\n')
}

const DATA_KEY = /^[A-Za-z0-9_-]{43}$/
const CODE = /^[0-9A-Z]{4}(?:-[0-9A-Z]{4}){3}$/i

/** Parse a kit file (or an older "Viko recovery codes" note). Never throws with file contents. */
export function parseRecoveryKit(text: string): Omit<RecoveryKit, 'createdAt'> {
  if (typeof text !== 'string' || text.length > 16_384) throw new Error('Not a Viko recovery kit')
  const lines = text.split(/\r?\n/).map((l) => l.trim())
  const accountId = lines
    .find((l) => l.toLowerCase().startsWith('account id:'))
    ?.slice('account id:'.length)
    .trim()
  const email = lines
    .find((l) => l.toLowerCase().startsWith('email:'))
    ?.slice('email:'.length)
    .trim()
  const codes = lines.filter((l) => CODE.test(l)).slice(0, 16)
  const dataKey = lines.find((l) => DATA_KEY.test(l))
  if (!accountId || accountId.length > 256 || (!codes.length && !dataKey))
    throw new Error('Not a Viko recovery kit')
  return { accountId, codes, ...(email ? { email } : {}), ...(dataKey ? { dataKey } : {}) }
}

export function recoveryKitSaveRequest(kit: RecoveryKit): SaveRequest {
  return {
    title: KIT_TITLE,
    fields: [
      {
        // The enum is declared const by the upstream package; no runtime enum exists.
        autocomplete: 'username' as SaveRequest['fields'][number]['autocomplete'],
        value: kit.email?.trim() || kit.accountId,
      },
    ],
    notes: recoveryKitText(kit),
  }
}

export function downloadRecoveryKit(kit: RecoveryKit) {
  const url = URL.createObjectURL(new Blob([recoveryKitText(kit)], { type: 'text/plain' }))
  const a = document.createElement('a')
  a.href = url
  a.download = 'viko-recovery-kit.txt'
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}

// ---- sign-up: escrows prepared before genesis --------------------------------------------------

type Prepared = Awaited<ReturnType<typeof createDirectoryEscrow>>
const pendingKey = (accountId: string) => `recovery-kit/pending/v1/${accountId}`
const PendingRecord = z.object({ escrows: z.array(z.any()).max(4), createdAt: z.number() })

/**
 * Create the data recovery key (returned once, never stored) and, when the passkey registration
 * returned PRF, the v2 PRF escrow for that passkey. Both are held as ciphertext until genesis.
 * Uses this browser's own registration pins (pinLocalRegistration) as the escrowed trust anchors.
 */
export async function preparePendingKit(
  accountId: string,
  prf?: { credentialId: string; output: Uint8Array },
): Promise<string> {
  const trust = await loadContentTrust(accountId)
  const dataKey = createRecoveryCode()
  const escrows: Prepared[] = [
    await createDirectoryEscrow({
      accountId,
      kind: 'recovery',
      kek: await deriveRecoveryKek(dataKey, accountId),
      credentials: trust.credentials(),
      rpId: trust.rp.rpId,
    }),
  ]
  if (prf && trust.credentials().some((c) => c.credentialId === prf.credentialId))
    escrows.push(
      await createDirectoryEscrow({
        accountId,
        kind: 'prf',
        kek: await derivePrfKek(prf.output, accountId, prf.credentialId),
        credentialId: prf.credentialId,
        credentials: trust.credentials(),
        rpId: trust.rp.rpId,
        prfSalt: 2,
      }),
    )
  await idb.set(pendingKey(accountId), { escrows, createdAt: Date.now() })
  return dataKey
}

export async function pendingKit(accountId: string): Promise<Prepared[]> {
  const raw = await idb.get(pendingKey(accountId))
  const parsed = PendingRecord.safeParse(raw)
  return parsed.success ? (parsed.data.escrows as Prepared[]) : []
}

export async function clearPendingKit(accountId: string) {
  await idb.del(pendingKey(accountId))
}

/**
 * Genesis hook (content-session.ts): the pending escrow recipients join the genesis directory,
 * and after it is published each escrow is uploaded with its epoch-1 wrap.
 */
export async function genesisEscrows(accountId: string) {
  const pending = await pendingKit(accountId).catch(() => [])
  const valid = pending.filter((p) => p.record.accountId === accountId)
  return {
    recipients: valid.map((p) => p.recipient) as KeyRecipient[],
    escrowKeys: valid.map((p) => ({
      recipientId: p.recipient.id,
      publicKey: p.record.publicKey as EcPublicJwk,
    })),
    async publish(api: Pick<Api, 'contentKeys'>, raw: Uint8Array) {
      for (const p of valid)
        await keysApi(api).publishEscrow({
          escrow: p.record,
          wraps: [
            {
              epoch: 1,
              wrap: await wrapAccountKey(
                raw,
                { version: 1, accountId, epoch: 1, recipientId: p.recipient.id },
                p.record.publicKey,
              ),
            },
          ],
        })
      if (valid.length) await clearPendingKit(accountId)
    },
  }
}

/** A kit prepared at sign-up whose genesis happened elsewhere: add it now (one passkey tap). */
export function activatePendingKit(session: BrowserContentSession) {
  return session.exclusive(async (ctx) => {
    const pending = await pendingKit(ctx.accountId)
    for (const p of pending) {
      const { trust, device, checkpoint } = await unlocked(ctx)
      await addEscrow(ctx, trust, device, checkpoint, {
        kind: p.record.kind,
        prepared: p,
        ...(p.record.kind === 'prf' ? { credentialId: p.recipient.id } : {}),
      })
    }
    await clearPendingKit(ctx.accountId)
    return { activated: pending.length }
  })
}

// ---- status and rotation -----------------------------------------------------------------------

export type KitStatus = 'active' | 'pending' | 'missing' | 'unknown'

/** From this browser's verified directory checkpoint (never a relay claim). */
export async function recoveryKitStatus(session: BrowserContentSession): Promise<KitStatus> {
  try {
    const accountId = await session.accountId()
    const state = await session.vault.state(accountId)
    if (state?.checkpoint?.statement.recipients.some((r) => r.type === 'recovery')) return 'active'
    if ((await pendingKit(accountId)).length) return 'pending'
    return state?.checkpoint ? 'missing' : 'unknown'
  } catch {
    return 'unknown'
  }
}

/** Fresh step-up for the hub's code rotation, only when the session's window has passed. */
async function withStepUp<T>(
  api: Pick<Api, 'stepUpOptions' | 'stepUpVerify'>,
  passkeys: Pick<PasskeyProvider, 'login'>,
  run: () => Promise<T>,
): Promise<T> {
  try {
    return await run()
  } catch (e) {
    if (!(e instanceof ApiError && e.code === 'step_up_required')) throw e
    await api.stepUpVerify(await passkeys.login(await api.stepUpOptions()))
    return run()
  }
}

/**
 * Regenerate the kit: a new data recovery key replaces the recovery recipient (epoch rotation;
 * the hub drops the old escrow) and every sign-in code is replaced. The old kit stops working.
 */
export async function rotateRecoveryKit(
  session: BrowserContentSession,
  api: Pick<Api, 'contentKeys' | 'stepUpOptions' | 'stepUpVerify' | 'session'>,
  passkeys: Pick<PasskeyProvider, 'login'>,
): Promise<RecoveryKit> {
  const dataKey = await session.exclusive(async (ctx) => {
    const { trust, device, checkpoint } = await unlocked(ctx)
    const code = createRecoveryCode()
    await addEscrow(ctx, trust, device, checkpoint, {
      kind: 'recovery',
      kek: await deriveRecoveryKek(code, ctx.accountId),
    })
    await clearPendingKit(ctx.accountId)
    return code
  })
  const { codes } = await withStepUp(api, passkeys, async () =>
    z
      .object({ codes: z.array(z.string()).min(1).max(16) })
      .parse(await api.contentKeys('recovery-kit/codes')),
  )
  const s = await api.session()
  return {
    accountId: await session.accountId(),
    ...(s.authenticated && s.email ? { email: s.email } : {}),
    codes,
    dataKey,
    createdAt: Date.now(),
  }
}

// ---- restore -----------------------------------------------------------------------------------

let remembered: Omit<RecoveryKit, 'createdAt'> | undefined
/** In memory only: carries a kit from sign-in to the post-passkey restore step. */
export function rememberKit(kit: Omit<RecoveryKit, 'createdAt'> | undefined) {
  remembered = kit
}
export const rememberedKit = () => remembered

const requestKey = (accountId: string) => `recovery-kit/request/v1/${accountId}`

/** Prove this session's device (so a held request can be finished from a later session). */
async function bindThisSession(api: Pick<Api, 'contentKeys'>, accountId: string, d: StoredDevice) {
  const relay = keysApi(api)
  const { nonce } = await relay.sessionChallenge()
  const statement = { version: 1, accountId, deviceKeyId: d.deviceKeyId, nonce }
  const signature = await signStatement(
    { sign: (data) => signP256(d.privateKey, data) },
    SESSION_DEVICE_DOMAIN,
    statement,
  )
  await relay.bindSession({ statement, signature }).catch((e) => {
    if (!(e instanceof ApiError && e.code === 'session_already_bound')) throw e
  })
}

/**
 * Restore encrypted work with the kit's data recovery key. Starts (or resumes) the held request;
 * once released, opens the escrow, verifies the directory from genesis, installs the keys and
 * adds this browser to the directory with its (new) passkey.
 */
export async function restoreWithKit(
  session: BrowserContentSession,
  api: Pick<Api, 'contentKeys'>,
  dataKey: string,
  deps: { endorse: () => Promise<StoredDevice>; origin?: string },
): Promise<RecoveryResult> {
  const accountId = await session.accountId()
  let device = await loadDevice()
  if (!device?.endorsement) device = await deps.endorse()
  await bindThisSession(api, accountId, device)
  const saved = await idb.get<{ id: string }>(requestKey(accountId))
  const attempt = (requestId?: string) =>
    recoverAccess(
      session,
      { kind: 'recovery', code: dataKey },
      {
        endorse: deps.endorse,
        ...(requestId ? { requestId } : {}),
        ...(deps.origin ? { origin: deps.origin } : {}),
      },
    )
  // Resume a request this browser started earlier; one that was denied, expired or completed
  // elsewhere is replaced by a new one (with a new hold).
  const earlier = saved
    ? (await keysApi(api).listRecovery()).requests.find((r) => r.id === saved.id)
    : undefined
  let result: RecoveryResult
  if (earlier?.status === 'held') result = { status: 'held', request: earlier }
  else if (earlier?.status === 'ready') result = await attempt(earlier.id)
  else {
    if (saved) await idb.del(requestKey(accountId))
    result = await attempt()
  }
  if (result.status === 'held') await idb.set(requestKey(accountId), { id: result.request.id })
  if (result.status === 'recovered') await idb.del(requestKey(accountId))
  return result
}

/** A held kit restore started on this browser (id only; the data key is never stored). */
export async function pendingKitRestore(accountId: string) {
  return (await idb.get<{ id: string }>(requestKey(accountId)))?.id
}
